import { Component, OnInit, AfterViewInit, ViewChildren, ViewChild, QueryList, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../shared/material.module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { LanguageService } from '../../services/language.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { VehicleColorsService } from '../../services/vehicle-colors.service';
import { PhotoPreviewDialogComponent } from '../photo-preview-dialog/photo-preview-dialog.component';
import { ActivatedRoute } from '@angular/router';
import { StepDriverVehicleDataComponent } from './steps/step-driver-vehicle-data/step-driver-vehicle-data.component';
import { StepInvolvedVehiclesComponent } from './steps/step-involved-vehicles/step-involved-vehicles.component';
import { StepAccidentDetailsComponent } from './steps/step-accident-details/step-accident-details.component';
import { StepAccidentDynamicsComponent } from './steps/step-accident-dynamics/step-accident-dynamics.component';
import { StepSummaryComponent } from './steps/step-summary/step-summary.component';
import { LanguageSelectorComponent } from '../language-selector/language-selector';
import { StepDamageDescriptionComponent } from './steps/step-damage-description/step-damage-description.component';
import { SuccessScreenComponent } from '../success-screen/success-screen.component';
import { CrashService } from '../../services/crash.service';
import { AuthService } from '../../services/auth.service';
import { UPLOAD_LIMITS } from '../../config/upload-limits.config';
import { VehicleUtils } from '../../utils/vehicle.utils';
import { WizardStepValidatorService, ClaimFlowMode } from '../../services/validator/wizard-step-validator.service';
import { GeolocationService } from '../../services/geolocation.service';
import { ClientConfigurationService } from '../../services/client-configuration.service';
import { CidService } from '../../services/cid.service';
import { NotificationService } from '../../services/notification.service';
import { TranscodeService } from '../../services/transcode.service';
import { CIDRequestDTO } from '../../models/cid.model';
import { Transcode } from '../../models/transcode.model';
import {
  getTypeDocIdByDescription,
  TYPE_DOC_CID,
} from '../../utils/type-doc.util';
import { ConfirmDialogService } from '../../shared/components/confirm-dialog/confirm-dialog.service';
import { generatePdfThumbnail } from '../../utils/pdf-preview.util';
import { firstValueFrom } from 'rxjs';
import { switchMap, finalize, tap } from 'rxjs/operators';

interface VehicleData {
  plate: string;
  circumstances: any[];
  photos: { file: File, url: string, name: string }[];
  isLoading: boolean;
}

@Component({
  selector: 'app-wizard-sinistro',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    TranslatePipe,
    LanguageSelectorComponent,
    StepDriverVehicleDataComponent,
    StepInvolvedVehiclesComponent,
    StepAccidentDynamicsComponent,
    StepAccidentDetailsComponent,
    StepDamageDescriptionComponent,
    StepSummaryComponent,
    SuccessScreenComponent,
  ],
  templateUrl: './wizard-sinistro.html',
  styleUrls: ['./wizard-sinistro.scss']
})
export class WizardSinistroComponent implements OnInit, AfterViewInit {

  @ViewChild(StepSummaryComponent) stepSummaryComponent!: StepSummaryComponent;

  @ViewChildren(MatExpansionPanel) expansionPanels!: QueryList<MatExpansionPanel>;
  @ViewChild(StepInvolvedVehiclesComponent) involvedVehiclesComponent!: StepInvolvedVehiclesComponent;
  @ViewChild(StepDamageDescriptionComponent) damageComponent!: StepDamageDescriptionComponent;
  @ViewChild(StepDriverVehicleDataComponent) driverVehicleComponent!: StepDriverVehicleDataComponent;
  @ViewChild('involvedVehiclesPanel') involvedVehiclesPanel!: MatExpansionPanel;
  @ViewChild('dynamicsPanel') dynamicsPanel!: MatExpansionPanel;

  driverVehicleForm!: FormGroup;
  involvedVehiclesForm!: FormGroup;
  accidentDynamicsForm!: FormGroup;
  dettagliSinistroForm!: FormGroup;
  danniForm!: FormGroup;
  fotoForm!: FormGroup;

  selectedFiles: File[] = [];
  previewUrls: string[] = [];
  witnessDocuments: { file: File; url: string; name: string }[] = [];
  witnessDocumentLoading = false;
  cidDocuments: { file: File; url: string; previewUrl: string; name: string; isPdf: boolean }[] = [];
  cidDocumentLoading = false;
  isCidSubmitting = false;
  private typeDocTranscodes: Transcode[] = [];
  private vehiclesData: VehicleData[] = [];
  private _vehiclePhotos: { [key: string]: { file: File, url: string, name: string }[] } = { A: [] };
  private _vehicleLoadingStates: { [key: string]: boolean } = { A: false };
  
  get vehiclePhotos(): { [key: string]: { file: File, url: string, name: string }[] } {
    return this._vehiclePhotos;
  }
  
  get vehicleLoadingStates(): { [key: string]: boolean } {
    return this._vehicleLoadingStates;
  }
  
  private updateCompatibilityProperties(): void {
    this._vehiclePhotos = { A: [] };
    this._vehicleLoadingStates = { A: false };
    
    this.vehiclesData.forEach((vehicle, index) => {
      const letter = index === 0 ? 'A' : this.vehicleLetters[index];
      this._vehiclePhotos[letter] = vehicle.photos;
      this._vehicleLoadingStates[letter] = vehicle.isLoading;
    });
  }
  vehicleLetters = VehicleUtils.getVehicleLetters();
  isLoadingLocation = false;
  isLocationReady = false;
  isLanguageSelected = false;
  claimFlowMode: ClaimFlowMode | null = null;
  isFlowSelected = false;
  isDriverVehicleValid: boolean = false;
  isInvolvedVehiclesValid: boolean = false;
  isAccidentDynamicsValid: boolean = false;
  isDettagliSinistroValid: boolean = false;
  isDanniValid: boolean = false;

  incidentLocation: { lat: number, lng: number, address: string } | null = null;
  currentLocation: { lat: number, lng: number, address: string } | null = null;
  private manualLocationSelected = false;
  isSubmitted = false;
  submittedSinistroId: number | null = null;
  showSuccessScreen = false;
  preselectedTarga: string = "";

  isCrashesInfoLoading = false;
  crashesInfoLoaded = false;
  crashesInfoData: any = null;
  crashesInfoFailed = false;
  vehicleId: number | null = null;
  isLoginLoading = false;
  isLoginCompleted = false;

  constructor(
    private fb: FormBuilder,
    private languageService: LanguageService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private vehicleColorsService: VehicleColorsService,
    private cdr: ChangeDetectorRef,
    private crashService: CrashService,
    private authService: AuthService,
    private stepValidator: WizardStepValidatorService,
    private geolocationService: GeolocationService,
    private clientConfigurationService: ClientConfigurationService,
    private cidService: CidService,
    private notificationService: NotificationService,
    private confirmDialogService: ConfirmDialogService,
    private transcodeService: TranscodeService
  ) { }

  ngOnInit() {
    const targaSnapshot = this.route.snapshot.paramMap.get('targa');
    if (targaSnapshot) {
      this.preselectedTarga = targaSnapshot.toUpperCase();
    }
    this.route.params.subscribe((params) => {
      if (params['targa']) {
        this.preselectedTarga = params['targa'].toUpperCase();
      }
    });

    this.initializeVehiclesData();
    this.initializeForms();
    this.setDefaultDateTime();
    this.setupLocationRequestOnFirstUserGesture();
    this.setupStepValidation();
    this.injectVehicleColors();
    this.authenticateAndLoadData();

    (window as any).wizardComponent = this;
  }

  private setupLocationRequestOnFirstUserGesture(): void {
    const trigger = () => {
      window.removeEventListener('click', trigger, true);
      document.removeEventListener('touchstart', trigger, true);
      this.requestLocationNative();
      this.cdr.detectChanges();
    };
    window.addEventListener('click', trigger, true);
    document.addEventListener('touchstart', trigger, true);
    this.cdr.detectChanges();
  }
  
  private initializeVehiclesData(): void {
    this.vehiclesData = [{
      plate: this.preselectedTarga,
      circumstances: [],
      photos: [],
      isLoading: false
    }];
  }

  onVehiclesConfirmed(): void {
    const stepComponent = this.getInvolvedVehiclesComponent();
    if (stepComponent && stepComponent.hasValidPlates() && !this.hasDuplicatePlates()) {
      this.isInvolvedVehiclesValid = true;
      this.initializeVehicleStructures();
      this.injectVehicleColors();
    }
  }
  
  public checkDuplicatePlates(): boolean {
    return this.hasDuplicatePlates();
  }
  
  public getVehicleAPlate(): string {
    return this.preselectedTarga;
  }
  
  private hasDuplicatePlates(): boolean {
    const involvedVehicles = this.getInvolvedVehicles();
    const plates = involvedVehicles.map(v => v.plate.toUpperCase().trim());
    const uniquePlates = new Set(plates);
    
    // Controllo anche se qualche targa coincide con il veicolo A
    if (this.preselectedTarga && plates.includes(this.preselectedTarga.toUpperCase())) {
      return true;
    }
    
    return plates.length !== uniquePlates.size;
  }

  private initializeVehicleStructures(): void {
    const involvedVehicles = this.getInvolvedVehicles();
    
    // Inizializzo foto per ogni veicolo
    involvedVehicles.forEach((_, index) => {
      const vehicleLetter = this.vehicleLetters[index + 1];
      if (!this.vehiclePhotos[vehicleLetter]) {
        this.vehiclePhotos[vehicleLetter] = [];
      }
      if (this.vehicleLoadingStates[vehicleLetter] === undefined) {
        this.vehicleLoadingStates[vehicleLetter] = false;
      }
    });
    
    // Aggiorno il form delle dinamiche per includere tutti i veicoli
    this.updateAccidentDynamicsForm();
  }

  private updateAccidentDynamicsForm(): void {
    const involvedVehicles = this.getInvolvedVehicles();
    const currentDynamicsGroup = this.accidentDynamicsForm.get('accidentDynamics') as FormGroup;
    
    if (currentDynamicsGroup) {
      // Aggiungi controlli per i nuovi veicoli
      involvedVehicles.forEach((_, index) => {
        const vehicleLetter = this.vehicleLetters[index + 1];
        const controlName = `vehicle${vehicleLetter}`;
        
        if (!currentDynamicsGroup.get(controlName)) {
          (currentDynamicsGroup as any).addControl(controlName, this.fb.control([], Validators.required));
        }
      });
    }
  }

  onVehiclesChanged(): void {
    this.isInvolvedVehiclesValid = false;
    this.syncVehiclesData();
  }
  
  private syncVehiclesData(): void {
    const currentVehicles = this.getInvolvedVehicles();
    const newVehiclesData: VehicleData[] = [];
    
    // Veicolo A (sempre primo)
    const vehicleAData = this.vehiclesData.find(v => v.plate === this.preselectedTarga) || {
      plate: this.preselectedTarga,
      circumstances: this.getVehicleCircumstances('A'),
      photos: this.getVehiclePhotosArray('A'),
      isLoading: false
    };
    newVehiclesData.push(vehicleAData);
    
    // Veicoli coinvolti - mantiene allineamento per targa
    currentVehicles.forEach(vehicle => {
      const existingData = this.vehiclesData.find(v => v.plate === vehicle.plate);
      newVehiclesData.push(existingData || {
        plate: vehicle.plate,
        circumstances: [],
        photos: [],
        isLoading: false
      });
    });
    
    this.vehiclesData = newVehiclesData;
    this.updateFormsFromVehiclesData();
  }

  private updateFormsFromVehiclesData(): void {
    const newDynamicsGroup = this.fb.group({});
    
    this.vehiclesData.forEach((vehicle, index) => {
      const letter = index === 0 ? 'A' : this.vehicleLetters[index];
      const controlName = `vehicle${letter}`;
      (newDynamicsGroup as any).addControl(controlName, this.fb.control(vehicle.circumstances, Validators.required));
    });
    
    this.accidentDynamicsForm.setControl('accidentDynamics', newDynamicsGroup);
    this.updateCompatibilityProperties();
    this.updateLegacyArrays();
  }
  
  private getVehicleCircumstances(letter: string): any[] {
    const dynamicsGroup = this.accidentDynamicsForm.get('accidentDynamics') as FormGroup;
    return dynamicsGroup?.get(`vehicle${letter}`)?.value || [];
  }
  
  private getVehiclePhotosArray(letter: string): { file: File, url: string, name: string }[] {
    return this.vehiclesData.find(v => v.plate === (letter === 'A' ? this.preselectedTarga : ''))?.photos || [];
  }
  
  closeInvolvedVehiclesAndOpenDynamics(): void {
    if (this.involvedVehiclesPanel) {
      this.involvedVehiclesPanel.close();
    }
    if (this.dynamicsPanel) {
      this.dynamicsPanel.open();
    }
  }

  ngAfterViewInit() {
    this.preventAutoScroll();
  }

  private preventAutoScroll() {
    if (this.expansionPanels) {
      this.expansionPanels.forEach(panel => {
        panel.opened.subscribe(() => {
          const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

          setTimeout(() => {
            window.scrollTo({
              top: currentScrollPosition,
              behavior: 'auto'
            });
          }, 0);
        });
      });
    }
  }

  private initializeForms() {
    this.driverVehicleForm = this.fb.group({
      telefono: ['', Validators.required], 
      selectedDriverId: ['', Validators.required],
      brand: [''],
      modelloBus: ['']
    });

    this.involvedVehiclesForm = this.fb.group({
      involvedVehicles: this.fb.array([])
    });

    this.accidentDynamicsForm = this.fb.group({
      accidentDynamics: this.fb.group({
        vehicleA: [[], Validators.required],
        vehicleB: [[], Validators.required]
      })
    });

    this.dettagliSinistroForm = this.fb.group({
      dataParte: ['', Validators.required],
      oraParte: ['', Validators.required],
      indirizzo: [''],
      indirizzoCorrente: [''], 
      note: ['', [Validators.maxLength(2000)]]
    });

    this.danniForm = this.fb.group({
      descrizioneDanni: [''], //, [Validators.required, Validators.minLength(10)]
      presenzaFeriti: ['', Validators.required],
      danniMateriali: ['', Validators.required],
      presenzaTestimoni: ['', Validators.required],
      witnesses: this.fb.array([])
    });

    this.fotoForm = this.fb.group({
      foto: ['']
    });
  }

  async onFileSelected(event: any, vehicleType: string) {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    const files = input.files;
    const maxSizeMB = UPLOAD_LIMITS.MAX_FILE_SIZE_MB;
    const maxTotalSizeMB = UPLOAD_LIMITS.MAX_TOTAL_SIZE_MB;
    const newPhotos: { file: File, url: string, name: string }[] = [];
    
    // Trovo il veicolo nella struttura dati
    const vehicleIndex = vehicleType === 'A' ? 0 : this.vehicleLetters.indexOf(vehicleType);
    if (vehicleIndex >= 0 && vehicleIndex < this.vehiclesData.length) {
      this.vehiclesData[vehicleIndex].isLoading = true;
    }
    
    if (!this.vehiclePhotos[vehicleType]) {
      this._vehiclePhotos[vehicleType] = [];
    }

    const allPhotos = Object.values(this.vehiclePhotos).flat();
    const currentTotalSize = allPhotos.reduce((total, photo) => total + photo.file.size, 0);

    let processedCount = 0;
    const totalNewFiles = Array.from(files).filter(file => {
      const isImage = file.type.startsWith('image/');
      const isLivePhoto = file.name.toLowerCase().includes('.heic') || 
                         file.name.toLowerCase().includes('.heif');
      
      if (!isImage && !isLivePhoto) {
        this.snackBar.open(`${file.name} non è un'immagine valida`, 'Chiudi', { duration: 3000 });
        return false;
      }

      return true;
    });

    if (totalNewFiles.length === 0) {
      this.vehicleLoadingStates[vehicleType] = false;
      return;
    }

    for (let file of totalNewFiles) {
      try {
        const originalSize = file.size;
        /*console.info(`Elaborazione ${file.name}:`, {
          dimensioneOriginale: `${(originalSize / 1024 / 1024).toFixed(2)}MB`,
          tipo: file.type
        });*/

        // Comprimo l'immagine
        const compressedFile = await this.compressImage(file, maxSizeMB);
        const compressedSize = compressedFile.size;
        
        /*console.info(`Compressione completata per ${file.name}:`, {
          dimensioneOriginale: `${(originalSize / 1024 / 1024).toFixed(2)}MB`,
          dimensioneCompressa: `${(compressedSize / 1024 / 1024).toFixed(2)}MB`,
          percentualeRiduzione: `${(((originalSize - compressedSize) / originalSize) * 100).toFixed(1)}%`
        });*/

        // Verifico limite totale
        if (currentTotalSize + compressedSize > maxTotalSizeMB * 1024 * 1024) {
          this.snackBar.open(`Limite totale di ${maxTotalSizeMB}MB raggiunto`, 'Chiudi', { duration: 3000 });
          break;
        }

        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substr(2, 5);
        const extension = file.name.split('.').pop();
        const newFileName = `${vehicleType}_${timestamp}_${randomId}.${extension}`;
        
        const renamedFile = new File([compressedFile], newFileName, { type: compressedFile.type });

        const reader = new FileReader();
        reader.onload = (e: any) => {
          newPhotos.push({
            file: renamedFile,
            url: e.target.result,
            name: newFileName
          });
          processedCount++;
          
          if (processedCount === totalNewFiles.length) {
            // Aggiorna solo vehiclesData, poi sincronizza
            const vehicleIndex = vehicleType === 'A' ? 0 : this.vehicleLetters.indexOf(vehicleType);
            if (vehicleIndex >= 0 && vehicleIndex < this.vehiclesData.length) {
              this.vehiclesData[vehicleIndex].photos.push(...newPhotos);
              this.vehiclesData[vehicleIndex].isLoading = false;
            }
            
            this.updateCompatibilityProperties();
            this.updateLegacyArrays();
          }
        };
        reader.readAsDataURL(renamedFile);
        
      } catch (error) {
        console.error(`Errore compressione ${file.name}:`, error);
        this.snackBar.open(`Errore elaborazione ${file.name}`, 'Chiudi', { duration: 3000 });
        processedCount++;
        
        if (processedCount === totalNewFiles.length) {
          const vehicleIndex = vehicleType === 'A' ? 0 : this.vehicleLetters.indexOf(vehicleType);
          if (vehicleIndex >= 0 && vehicleIndex < this.vehiclesData.length) {
            this.vehiclesData[vehicleIndex].isLoading = false;
          }
          this.updateCompatibilityProperties();
        }
      }
    }
    
    input.value = '';
  }

  private async compressImage(file: File, maxSizeMB: number): Promise<File> {
    const imageCompression = (await import('browser-image-compression')).default;
    
    const options = {
      maxSizeMB: maxSizeMB,
      maxWidthOrHeight: UPLOAD_LIMITS.MAX_IMAGE_RESOLUTION,
      useWebWorker: true,     //  il lavoro "pesante" di compressione viene eseeguito in un thred separato per non bloccare l'UI
      fileType: file.type,
      initialQuality: UPLOAD_LIMITS.COMPRESSION_QUALITY
    };

    return await imageCompression(file, options);
  }

  openImagePreview(url: string): void {
    this.openFilePreview({ url, isPdf: false });
  }

  openCidDocumentPreview(doc: { url: string; previewUrl: string; isPdf: boolean; name: string }): void {
    const previewSource = doc.isPdf ? doc.previewUrl : doc.url;
    if (!previewSource) return;
    this.openFilePreview({ url: previewSource, isPdf: doc.isPdf, fileName: doc.name });
  }

  private openFilePreview(options: { url: string; isPdf: boolean; fileName?: string }): void {
    this.dialog.open(PhotoPreviewDialogComponent, {
      data: {
        imageUrl: options.url,
        isPdf: options.isPdf,
        fileName: options.fileName,
      },
      width: options.isPdf ? '90vw' : '80vw',
      maxWidth: options.isPdf ? '960px' : '800px',
      maxHeight: '90vh',
      panelClass: 'custom-dialog-container',
      disableClose: false,
      autoFocus: true,
      restoreFocus: true,
    });
  }

  removeVehicleImage(vehicleType: string, index: number): void {
    // Aggiorna solo vehiclesData, poi sincronizza
    const vehicleIndex = vehicleType === 'A' ? 0 : this.vehicleLetters.indexOf(vehicleType);
    if (vehicleIndex >= 0 && vehicleIndex < this.vehiclesData.length) {
      this.vehiclesData[vehicleIndex].photos.splice(index, 1);
    }
    
    this.updateCompatibilityProperties();
    this.updateLegacyArrays();
  }

  private updateLegacyArrays(): void {
    const allPhotos = Object.values(this.vehiclePhotos).flat();
    this.selectedFiles = allPhotos.map(p => p.file);
    this.previewUrls = allPhotos.map(p => p.url);
  }

  getTotalPhotos(): number {
    return Object.values(this.vehiclePhotos).reduce((total, photos) => total + photos.length, 0);
  }

  getVehiclePhotos(vehicleType: string): { file: File, url: string, name: string }[] {
    return this.vehiclePhotos[vehicleType] || [];
  }

  isVehicleLoading(vehicleType: string): boolean {
    return this.vehicleLoadingStates[vehicleType] || false;
  }

  onSubmitSuccess(response: any): void {
    this.submittedSinistroId = response.id;
    this.isSubmitted = true;
    this.showSuccessScreen = true;
  }

  isFormValid(): boolean {
    return this.isLanguageSelected &&
      this.isDriverVehicleValid &&
      this.isDettagliSinistroValid &&
      this.isInvolvedVehiclesValid &&
      this.isAccidentDynamicsValid &&
      this.isDanniValid &&
      this.isFotoValid();
  }

  getValidationMessage(): string {
    if (!this.driverVehicleForm.valid) return 'Completare i dati autista e veicolo';
    if (!this.dettagliSinistroForm.valid) return 'Completare i dettagli del sinistro';
    if (!this.danniForm.valid) return 'Completare la descrizione dei danni';

    return '';
  }

  selectClaimFlow(mode: ClaimFlowMode): void {
    void this.onClaimFlowSelected(mode);
  }

  async onClaimFlowSelected(mode: ClaimFlowMode): Promise<void> {
    if (this.isFlowSelected && this.claimFlowMode === mode) {
      return;
    }

    if (this.isFlowSelected && this.claimFlowMode !== mode && this.hasProgressInCurrentFlow()) {
      const confirmed = await this.confirmFlowChange();
      if (!confirmed) {
        return;
      }
      this.clearFlowSpecificData(this.claimFlowMode);
    }

    this.claimFlowMode = mode;
    this.isFlowSelected = true;
    this.cdr.detectChanges();
  }

  hasProgressInCurrentFlow(): boolean {
    if (this.claimFlowMode === 'full') {
      return this.hasFullFlowProgress();
    }
    if (this.claimFlowMode === 'cid-upload') {
      return this.hasCidFlowProgress();
    }
    return false;
  }

  private hasFullFlowProgress(): boolean {
    return (
      this.hasDettagliSinistroUserProgress() ||
      this.getInvolvedVehicles().length > 0 ||
      this.hasAccidentDynamicsUserProgress() ||
      this.hasDanniUserProgress() ||
      this.getTotalPhotos() > 0 ||
      this.witnessDocuments.length > 0
    );
  }

  private hasDettagliSinistroUserProgress(): boolean {
    const hasNote = !!this.dettagliSinistroForm.get('note')?.value?.trim();
    return hasNote || this.manualLocationSelected;
  }

  private hasAccidentDynamicsUserProgress(): boolean {
    const dynamicsGroup = this.accidentDynamicsForm.get('accidentDynamics') as FormGroup | null;
    if (!dynamicsGroup) {
      return false;
    }

    return Object.keys(dynamicsGroup.controls).some(key => {
      const value = dynamicsGroup.get(key)?.value;
      return Array.isArray(value) && value.length > 0;
    });
  }

  private hasDanniUserProgress(): boolean {
    const form = this.danniForm;

    if (form.get('descrizioneDanni')?.value?.trim()) {
      return true;
    }
    if (form.get('presenzaFeriti')?.value) {
      return true;
    }
    if (form.get('danniMateriali')?.value) {
      return true;
    }
    if (form.get('presenzaTestimoni')?.value) {
      return true;
    }

    const witnesses = form.get('witnesses') as FormArray;
    return witnesses.length > 0;
  }

  private hasCidFlowProgress(): boolean {
    return this.cidDocuments.length > 0;
  }

  private async confirmFlowChange(): Promise<boolean> {
    return firstValueFrom(
      this.confirmDialogService.open({
        title: this.languageService.translate('initialOptions.changeFlow.title'),
        message: this.languageService.translate('initialOptions.changeFlow.message'),
        confirmLabel: this.languageService.translate('initialOptions.changeFlow.confirm'),
        cancelLabel: this.languageService.translate('button.cancel'),
        confirmColor: 'primary',
        icon: 'swap_horiz',
        variant: 'info',
      })
    );
  }

  private clearFlowSpecificData(mode: ClaimFlowMode | null): void {
    if (mode === 'full') {
      this.clearFullFlowData();
    } else if (mode === 'cid-upload') {
      this.clearCidFlowData();
    }
  }

  private clearFullFlowData(): void {
    this.dettagliSinistroForm.reset();
    this.setDefaultDateTime();

    const involvedArray = this.involvedVehiclesForm.get('involvedVehicles') as FormArray;
    involvedArray.clear();

    this.accidentDynamicsForm.setControl(
      'accidentDynamics',
      this.fb.group({
        vehicleA: [[], Validators.required],
        vehicleB: [[], Validators.required],
      })
    );

    this.danniForm.reset();
    const witnessesArray = this.danniForm.get('witnesses') as FormArray;
    witnessesArray.clear();

    this.fotoForm.reset();
    this.selectedFiles = [];
    this.previewUrls = [];
    this._vehiclePhotos = { A: [] };
    this._vehicleLoadingStates = { A: false };
    this.clearWitnessDocument();
    this.vehiclesData = [{ plate: this.preselectedTarga, circumstances: [], photos: [], isLoading: false }];
    this.manualLocationSelected = false;
    (window as any).manualLocationSelected = false;

    this.isInvolvedVehiclesValid = false;
    this.isAccidentDynamicsValid = false;
    this.isDettagliSinistroValid = false;
    this.isDanniValid = false;

    this.restoreIncidentLocationFromCrashesInfo();
    this.updateDettagliSinistroValidation();
    this.updateDanniValidation();

    if (this.involvedVehiclesComponent) {
      this.involvedVehiclesComponent.isConfirmed = false;
    }

    if (this.damageComponent) {
      this.damageComponent.witnesses = [{ nome: '', cognome: '', email: '', telefono: '', dichiarazione: '' }];
      this.damageComponent.phoneValidationStates = [true];
      this.damageComponent.emailValidationStates = [true];
    }
  }

  private clearCidFlowData(): void {
    this.revokeAllCidDocumentUrls();
    this.cidDocuments = [];
    this.cidDocumentLoading = false;
    this.isCidSubmitting = false;
  }

  private revokeCidDocumentUrl(doc: { url: string; previewUrl: string; isPdf: boolean }): void {
    if (doc.isPdf && doc.previewUrl) {
      URL.revokeObjectURL(doc.previewUrl);
    }
  }

  private revokeAllCidDocumentUrls(): void {
    this.cidDocuments.forEach(doc => this.revokeCidDocumentUrl(doc));
  }

  private restoreIncidentLocationFromCrashesInfo(): void {
    if (this.crashesInfoData?.lastPosition) {
      this.incidentLocation = {
        lat: this.crashesInfoData.lastPosition.lat,
        lng: this.crashesInfoData.lastPosition.lon,
        address: this.crashesInfoData.lastPosition.address || '',
      };
      this.dettagliSinistroForm.patchValue({
        indirizzo: this.crashesInfoData.lastPosition.address || '',
      });
    } else {
      this.incidentLocation = null;
      this.dettagliSinistroForm.patchValue({ indirizzo: '' });
    }
  }

  isFullFlow(): boolean {
    return this.claimFlowMode === 'full';
  }

  isCidUploadFlow(): boolean {
    return this.claimFlowMode === 'cid-upload';
  }

  getCidDocuments(): { file: File; url: string; previewUrl: string; name: string; isPdf: boolean }[] {
    return this.cidDocuments;
  }

  async onCidDocumentSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const maxSizeBytes = UPLOAD_LIMITS.MAX_FILE_SIZE_MB * 1024 * 1024;
    const maxTotalSizeBytes = UPLOAD_LIMITS.MAX_TOTAL_SIZE_MB * 1024 * 1024;
    let currentTotalSize = this.cidDocuments.reduce((total, doc) => total + doc.file.size, 0);

    const filesToProcess = Array.from(input.files).filter(file => {
      if (!this.isValidCidDocument(file)) {
        this.snackBar.open(`${file.name} non è un file valido`, 'Chiudi', { duration: 3000 });
        return false;
      }
      if (file.size > maxSizeBytes) {
        this.snackBar.open(
          `${file.name} supera il limite di ${UPLOAD_LIMITS.MAX_FILE_SIZE_MB}MB`,
          'Chiudi',
          { duration: 3000 }
        );
        return false;
      }
      const isDuplicate = this.cidDocuments.some(doc => doc.file.name === file.name);
      if (isDuplicate) {
        this.snackBar.open(`${file.name} è già stato caricato`, 'Chiudi', { duration: 3000 });
        return false;
      }
      return true;
    });

    if (filesToProcess.length === 0) {
      input.value = '';
      return;
    }

    this.cidDocumentLoading = true;
    const newDocuments: { file: File; url: string; previewUrl: string; name: string; isPdf: boolean }[] = [];

    try {
      for (let i = 0; i < filesToProcess.length; i++) {
        const file = filesToProcess[i];
        if (currentTotalSize + file.size > maxTotalSizeBytes) {
          this.snackBar.open(`Limite totale di ${UPLOAD_LIMITS.MAX_TOTAL_SIZE_MB}MB raggiunto`, 'Chiudi', { duration: 3000 });
          break;
        }

        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

        if (isPdf) {
          const previewUrl = URL.createObjectURL(file);
          try {
            const url = await generatePdfThumbnail(file);
            newDocuments.push({ file, url, previewUrl, name: file.name, isPdf });
          } catch (thumbnailError) {
            console.error('Errore anteprima PDF:', thumbnailError);
            URL.revokeObjectURL(previewUrl);
          }
        } else {
          const url = await this.readFileAsDataUrl(file);
          newDocuments.push({ file, url, previewUrl: url, name: file.name, isPdf });
        }

        currentTotalSize += file.size;
      }

      this.cidDocuments.push(...newDocuments);
    } catch (error) {
      console.error('Errore caricamento modulo CID:', error);
      this.snackBar.open('Errore durante il caricamento dei file', 'Chiudi', { duration: 3000 });
    } finally {
      this.cidDocumentLoading = false;
      input.value = '';
      this.cdr.detectChanges();
    }
  }

  removeCidDocument(index: number): void {
    const doc = this.cidDocuments[index];
    if (doc) {
      this.revokeCidDocumentUrl(doc);
    }
    this.cidDocuments.splice(index, 1);
    this.cdr.detectChanges();
  }

  onSubmitCidUpload(): void {
    if (this.isCidSubmitting || !this.canEnterCidSubmit()) return;

    this.isCidSubmitting = true;
    const crashData = this.buildCidUploadPayload();
    const allFiles = this.cidDocuments.map(doc => doc.file);
    const cidTypeId = getTypeDocIdByDescription(this.typeDocTranscodes, TYPE_DOC_CID);

    if (!cidTypeId) {
      //this.notificationService.showApiError('Tipologia CID non disponibile.');
      console.error('Tipologia CID non disponibile.');
      this.isCidSubmitting = false;
      return;
    }

    if (!this.vehicleId) {
      //this.notificationService.showApiError('Errore: ID veicolo non disponibile.');
      console.error('Errore: ID veicolo non disponibile.');
      this.isCidSubmitting = false;
      return;
    }

    this.cidService.createCIDcomplete(
      crashData,
      allFiles,
      this.vehicleId,
      allFiles.map(() => cidTypeId)
    ).subscribe({
      next: (response) => {
        this.isCidSubmitting = false;
        this.onSubmitSuccess(response);
      },
      error: (error) => {
        this.isCidSubmitting = false;
        console.error('Errore nel salvataggio del sinistro CID:', error);
        this.notificationService.showApiError('Si è verificato un errore durante il salvataggio del sinistro.');
      },
    });
  }

  private buildCidUploadPayload(): CIDRequestDTO {
    const driverVehicleData = this.driverVehicleForm.value;
    const selectedDriverId = driverVehicleData.selectedDriverId;
    const payload: any = {
      datecrash: new Date().toISOString(),
      note: '',
      locationLat: this.currentLocation?.lat?.toString() || '0',
      locationLon: this.currentLocation?.lng?.toString() || '0',
      locationAddress: this.currentLocation?.address || '',
      lat: this.incidentLocation?.lat?.toString() || '0',
      lon: this.incidentLocation?.lng?.toString() || '0',
      address: this.incidentLocation?.address || '',
      vehicleA: {
        plate: this.preselectedTarga || '',
        brand: driverVehicleData.brand || undefined,
        model: driverVehicleData.modelloBus || undefined,
      },
      vehicleB: [],
      wounded: false,
      physicalDamage: false,
      isWitnesses: false,
      fromApp: true,
      scannedCid: true,
    };

    if (selectedDriverId) {
      payload.newDriver = {
        id: selectedDriverId,
        phoneNumber: driverVehicleData.telefono || null,
      };
    } else if (this.crashesInfoData?.driverInfo?.driverId) {
      payload.newDriver = {
        id: this.crashesInfoData.driverInfo.driverId,
        phoneNumber: driverVehicleData.telefono || null,
      };
    } else {
      payload.newDriver = {
        id: null,
        phoneNumber: driverVehicleData.telefono || null,
      };
    }

    return payload;
  }

  private isValidCidDocument(file: File): boolean {
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isLivePhoto = file.name.toLowerCase().includes('.heic') ||
      file.name.toLowerCase().includes('.heif');
    return isImage || isPdf || isLivePhoto;
  }

  resetForm() {
    this.driverVehicleForm.reset();
    this.dettagliSinistroForm.reset();
    this.danniForm.reset();
    this.fotoForm.reset();
    this.selectedFiles = [];
    this.previewUrls = [];
    this._vehiclePhotos = { A: [] };
    this._vehicleLoadingStates = { A: false };
    this.clearWitnessDocument();
    this.revokeAllCidDocumentUrls();
    this.cidDocuments = [];
    this.cidDocumentLoading = false;
    this.isCidSubmitting = false;
    this.claimFlowMode = null;
    this.isFlowSelected = false;
    this.vehiclesData = [{ plate: this.preselectedTarga, circumstances: [], photos: [], isLoading: false }];
    this.incidentLocation = null;
    this.currentLocation = null;
    this.manualLocationSelected = false;

    (window as any).manualLocationSelected = false;
  }

  onIncidentLocationSelected(location: { lat: number, lng: number, address: string }) {
    this.incidentLocation = location;
    this.manualLocationSelected = true;
    this.isLoadingLocation = false;

    this.dettagliSinistroForm.patchValue({
      indirizzo: location.address
    });

    this.isLocationReady = true;
    this.updateDettagliSinistroValidation();
  }

  private requestLocationNative() {
    this.isLoadingLocation = true;
    this.cdr.detectChanges();

    this.geolocationService.getCurrentPositionQuick().subscribe({
      next: (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        this.geolocationService.getAddress(lat, lng).subscribe({
          next: (address) => {
            this.currentLocation = { lat, lng, address };
            this.dettagliSinistroForm.patchValue({ indirizzoCorrente: address });
            this.isLoadingLocation = false;
            this.isLocationReady = true;
            this.updateDettagliSinistroValidation();
            this.cdr.detectChanges();
          },
          error: () => {
            this.currentLocation = { lat, lng, address: `${lat.toFixed(5)}, ${lng.toFixed(5)}` };
            this.dettagliSinistroForm.patchValue({ indirizzoCorrente: this.currentLocation.address });
            this.isLoadingLocation = false;
            this.isLocationReady = true;
            this.updateDettagliSinistroValidation();
            this.cdr.detectChanges();
          }
        });
      },
      error: () => {
        // Se la posizione non è abilitata, provo a richiamare direttamente l'API nativa per forzare il popup del browser
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            () => {},
            () => {},
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
          );
        }
        this.isLoadingLocation = false;
        this.cdr.detectChanges();
      }
    });
  }

  onCurrentLocationSelected(location: { lat: number, lng: number, address: string }) {
    this.currentLocation = location;
    this.isLocationReady = true;
    this.dettagliSinistroForm.patchValue({
      indirizzoCorrente: location.address
    });
    this.updateDettagliSinistroValidation();
  }

  private setDefaultDateTime() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    this.dettagliSinistroForm.patchValue({
      dataParte: `${year}-${month}-${day}`,
      oraParte: `${hours}:${minutes}`
    });
  }

  getSummary() {
    const driverVehicleData = this.driverVehicleForm.value;
    const dettagliData = this.dettagliSinistroForm.value;
    const danniData = this.danniForm.value;
    const involvedVehicles = this.getInvolvedVehicles();
    const dynamicsData = this.accidentDynamicsForm.get('accidentDynamics')?.value || {};
    const vehicleACircumstances = dynamicsData.vehicleA || [];
    const vehicleBData = involvedVehicles.map((vehicle, index) => {
      const vehicleLetter = this.vehicleLetters[index + 1];
      const circumstances = dynamicsData[`vehicle${vehicleLetter}`] || [];
      return {
        plate: vehicle.plate,
        circumstances: circumstances,
        nome: vehicle.nome,
        cognome: vehicle.cognome,
        indirizzo: vehicle.indirizzo,
        telefono: vehicle.telefono,
      };
    });

    return {
      // Dati autista
      selectedDriverId: driverVehicleData.selectedDriverId,
      telefono: driverVehicleData.telefono,
      
      // Dati veicolo A (nostro)
      targa: this.preselectedTarga,
      brand: driverVehicleData.brand,
      modelloBus: driverVehicleData.modelloBus,

      // Dati dettagli sinistro
      ...dettagliData,
      dataOra: this.getDataOraFromForm(),
      latitudine: this.incidentLocation?.lat || null,
      longitudine: this.incidentLocation?.lng || null,
      
      // Dati posizione attuale (marker rosso)
      lat: this.currentLocation?.lat || 0,
      lon: this.currentLocation?.lng || 0,
      address: this.currentLocation?.address || '',

      // Dati danni
      descrizioneDanni: danniData.descrizioneDanni,
      danniVistaFrontale: danniData.danniVistaFrontale,
      danniVistaSinistra: danniData.danniVistaSinistra,
      danniVistaDestro: danniData.danniVistaDestro,
      danniVistaRetro: danniData.danniVistaRetro,

      vehicleA: {
        plate: this.preselectedTarga,
        circumstances: vehicleACircumstances
      },
      vehicleB: vehicleBData,
      involvedVehicles: involvedVehicles,

      numeroFoto: this.getTotalPhotos(),
      vehiclePhotos: this.vehiclePhotos
    };
  }

  getDataOraFromForm(): string | null {
    const dataParte = this.dettagliSinistroForm.get('dataParte')?.value;
    const oraParte = this.dettagliSinistroForm.get('oraParte')?.value;

    if (!dataParte || !oraParte) return null;

    return `${dataParte}T${oraParte}`;
  }

  private setupStepValidation() {
    this.languageService.getCurrentLanguage$().subscribe(() => {
      this.isLanguageSelected = true;
    });

    this.driverVehicleForm.statusChanges.subscribe(status => {
      this.isDriverVehicleValid = this.validateDriverForm();
    });

    this.driverVehicleForm.valueChanges.subscribe(() => {
      this.isDriverVehicleValid = this.validateDriverForm();
    });

    this.isInvolvedVehiclesValid = false;

    this.accidentDynamicsForm.statusChanges.subscribe(status => {
      this.isAccidentDynamicsValid = status === 'VALID';
    });

    this.dettagliSinistroForm.statusChanges.subscribe(() => {
      this.updateDettagliSinistroValidation();
    });

    this.danniForm.statusChanges.subscribe(() => {
      this.updateDanniValidation();
    });

    this.danniForm.valueChanges.subscribe(() => {
      if (this.danniForm.get('presenzaTestimoni')?.value !== 'true') {
        this.clearWitnessDocument();
      }
      this.updateDanniValidation();
    });
  }

  hasWitnesses(): boolean {
    return this.danniForm.get('presenzaTestimoni')?.value === 'true';
  }

  getWitnessDocuments(): { file: File; url: string; name: string }[] {
    return this.witnessDocuments;
  }

  async onWitnessDocumentSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const maxSizeBytes = UPLOAD_LIMITS.MAX_FILE_SIZE_MB * 1024 * 1024;
    const maxTotalSizeBytes = UPLOAD_LIMITS.MAX_TOTAL_SIZE_MB * 1024 * 1024;
    const allPhotos = Object.values(this.vehiclePhotos).flat();
    let currentTotalSize =
      allPhotos.reduce((total, photo) => total + photo.file.size, 0) +
      this.witnessDocuments.reduce((total, doc) => total + doc.file.size, 0);

    const filesToProcess = Array.from(input.files).filter(file => {
      if (!this.isValidWitnessDocument(file)) {
        this.snackBar.open(`${file.name} non è un'immagine valida`, 'Chiudi', { duration: 3000 });
        return false;
      }
      if (file.size > maxSizeBytes) {
        this.snackBar.open(
          `${file.name} supera il limite di ${UPLOAD_LIMITS.MAX_FILE_SIZE_MB}MB`,
          'Chiudi',
          { duration: 3000 }
        );
        return false;
      }
      const isDuplicate = this.witnessDocuments.some(doc => doc.file.name === file.name);
      if (isDuplicate) {
        this.snackBar.open(`${file.name} è già stato caricato`, 'Chiudi', { duration: 3000 });
        return false;
      }
      return true;
    });

    if (filesToProcess.length === 0) {
      input.value = '';
      return;
    }

    this.witnessDocumentLoading = true;
    const newDocuments: { file: File; url: string; name: string }[] = [];

    try {
      for (let i = 0; i < filesToProcess.length; i++) {
        const file = filesToProcess[i];
        if (currentTotalSize + file.size > maxTotalSizeBytes) {
          this.snackBar.open(`Limite totale di ${UPLOAD_LIMITS.MAX_TOTAL_SIZE_MB}MB raggiunto`, 'Chiudi', { duration: 3000 });
          break;
        }

        const url = await this.readFileAsDataUrl(file);
        newDocuments.push({ file, url, name: file.name });
        currentTotalSize += file.size;
      }

      this.witnessDocuments.push(...newDocuments);
    } catch (error) {
      console.error('Errore caricamento documento testimone:', error);
      this.snackBar.open('Errore durante il caricamento dei file', 'Chiudi', { duration: 3000 });
    } finally {
      this.witnessDocumentLoading = false;
      input.value = '';
      this.cdr.detectChanges();
    }
  }

  removeWitnessDocument(index: number): void {
    this.witnessDocuments.splice(index, 1);
    this.cdr.detectChanges();
  }

  private clearWitnessDocument(): void {
    this.witnessDocuments = [];
    this.witnessDocumentLoading = false;
  }

  private isValidWitnessDocument(file: File): boolean {
    const isImage = file.type.startsWith('image/');
    const isLivePhoto = file.name.toLowerCase().includes('.heic') ||
      file.name.toLowerCase().includes('.heif');
    return isImage || isLivePhoto;
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private updateDanniValidation(): void {
    const stepComponent = this.getDamageComponent();
    if (stepComponent) {
      const witnessesValid = stepComponent.validateWitnesses();
      const formValid = this.danniForm.status === 'VALID';
      const newValidState = formValid && witnessesValid;

      if (this.isDanniValid !== newValidState) {
        this.isDanniValid = newValidState;
        setTimeout(() => this.cdr.detectChanges(), 0);
      }
    } else {
      if (this.isDanniValid !== false) {
        this.isDanniValid = false;
        setTimeout(() => this.cdr.detectChanges(), 0);
      }
    }
  }

  private updateDettagliSinistroValidation() {
    const hasCurrentAddress = this.dettagliSinistroForm.get('indirizzoCorrente')?.value?.trim();
    const hasCurrentLocation = this.currentLocation !== null;
    this.isDettagliSinistroValid = this.dettagliSinistroForm.valid &&
                                   (hasCurrentAddress || hasCurrentLocation);
  }

  private authenticateAndLoadData(): void {
    sessionStorage.removeItem('auth_token');

    this.isLoginLoading = true;
    this.isLoginCompleted = false;

    this.authService
      .authenticate()
      .pipe(
        switchMap(() => this.transcodeService.getTranscodes('TYPE_DOC')),
        tap((typeDocs) => {
          this.typeDocTranscodes = typeDocs ?? [];
        }),
        switchMap(() => this.clientConfigurationService.loadConfiguration(this.preselectedTarga)),
        finalize(() => {
          this.isLoginLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => {
          this.isLoginCompleted = true;
          this.loadCrashesInfo();
        },
        error: (error) => {
          console.error('Authentication failed:', error);
          this.isLoginCompleted = false;
          this.loadCrashesInfo();
        },
      });
  }

  private loadCrashesInfo(): void {
    if (!this.preselectedTarga) {
      this.crashesInfoLoaded = true;
      return;
    }

    this.isCrashesInfoLoading = true;
    this.crashesInfoLoaded = false;

    this.crashService.getCrashesInfo(this.preselectedTarga).subscribe({
      next: (response) => {
        this.crashesInfoData = response;
        this.crashesInfoLoaded = true;
        this.isCrashesInfoLoading = false;
        this.vehicleId = response.vehicleInfo?.vehicleId || null;

        if (response.lastPosition) {
          this.incidentLocation = {
            lat: response.lastPosition.lat,
            lng: response.lastPosition.lon,
            address: response.lastPosition.address || ''
          };
          this.dettagliSinistroForm.patchValue({
            indirizzo: response.lastPosition.address || ''
          });
        }

        // Precompilo i dati del veicolo
        if (response.vehicleInfo) {
          this.driverVehicleForm.patchValue({
            brand: response.vehicleInfo.manufacturer || '',
            modelloBus: response.vehicleInfo.model || ''
          });
        }
      },
      error: (error) => {
        console.error('Errore API crashesInfo:', error);
        this.crashesInfoLoaded = true;
        this.crashesInfoFailed = true;
        this.isCrashesInfoLoading = false;
      }
    });
  }

  // Metodi per controllare se uno step può essere abilitato
  canEnterInitialOptions(): boolean {
    return this.stepValidator.canEnterInitialOptions(this.isLanguageSelected);
  }

  canEnterDriverVehicle(): boolean {
    return this.stepValidator.canEnterDriverVehicle(
      this.isLanguageSelected,
      this.isLoginCompleted,
      this.crashesInfoLoaded,
      this.isFlowSelected
    );
  }

  canEnterCidUpload(): boolean {
    return this.stepValidator.canEnterCidUpload(
      this.isLanguageSelected,
      this.isLoginCompleted,
      this.crashesInfoLoaded,
      this.isFlowSelected,
      this.claimFlowMode,
      this.isDriverVehicleValid
    );
  }

  isCidUploadValid(): boolean {
    return this.stepValidator.isCidUploadValid(this.cidDocuments);
  }

  canEnterCidSubmit(): boolean {
    return this.stepValidator.canEnterCidSubmit(
      this.isLanguageSelected,
      this.isLoginCompleted,
      this.crashesInfoLoaded,
      this.isFlowSelected,
      this.claimFlowMode,
      this.isDriverVehicleValid,
      this.isCidUploadValid()
    );
  }

  canEnterDettagliSinistro(): boolean {
    return this.stepValidator.canEnterDettagliSinistro(
      this.isLanguageSelected,
      this.isLoginCompleted,
      this.crashesInfoLoaded,
      this.isDriverVehicleValid,
      this.claimFlowMode
    );
  }

  canEnterInvolvedVehicles(): boolean {
    return this.stepValidator.canEnterInvolvedVehicles(
      this.isLanguageSelected,
      this.isLoginCompleted,
      this.crashesInfoLoaded,
      this.isDriverVehicleValid,
      this.isDettagliSinistroValid,
      this.claimFlowMode
    );
  }

  canEnterAccidentDynamics(): boolean {
    return this.stepValidator.canEnterAccidentDynamics(
      this.isLanguageSelected,
      this.isLoginCompleted,
      this.crashesInfoLoaded,
      this.isDriverVehicleValid,
      this.isDettagliSinistroValid,
      this.isInvolvedVehiclesValid,
      this.claimFlowMode
    );
  }

  private getInvolvedVehiclesComponent(): StepInvolvedVehiclesComponent | null {
    return this.involvedVehiclesComponent;
  }

  private getDamageComponent(): StepDamageDescriptionComponent | null {
    return this.damageComponent;
  }

  canEnterDanni(): boolean {
    return this.stepValidator.canEnterDanni(
      this.isLanguageSelected,
      this.isLoginCompleted,
      this.crashesInfoLoaded,
      this.isDriverVehicleValid,
      this.isDettagliSinistroValid,
      this.isInvolvedVehiclesValid,
      this.isAccidentDynamicsValid,
      this.claimFlowMode
    );
  }

  canEnterFoto(): boolean {
    return this.stepValidator.canEnterFoto(
      this.isLanguageSelected,
      this.isLoginCompleted,
      this.crashesInfoLoaded,
      this.isDriverVehicleValid,
      this.isDettagliSinistroValid,
      this.isInvolvedVehiclesValid,
      this.isAccidentDynamicsValid,
      this.isDanniValid,
      this.claimFlowMode
    );
  }

  isFotoValid(): boolean {
    return this.stepValidator.isFotoValid(this.vehiclePhotos, this.getInvolvedVehicles(), this.vehicleLetters);
  }

  canEnterRiepilogo(): boolean {
    return this.stepValidator.canEnterRiepilogo(
      this.isLanguageSelected,
      this.isLoginCompleted,
      this.crashesInfoLoaded,
      this.isDriverVehicleValid,
      this.isDettagliSinistroValid,
      this.isInvolvedVehiclesValid,
      this.isAccidentDynamicsValid,
      this.isDanniValid,
      this.isFotoValid(),
      this.claimFlowMode
    );
  }

  // Metodi per il progress indicator
  getTotalSteps(): number {
    return this.stepValidator.getTotalSteps(this.claimFlowMode);
  }

  getCompletedSteps(): number {
    return this.stepValidator.getCompletedSteps(
      this.claimFlowMode,
      this.isLanguageSelected,
      this.isFlowSelected,
      this.isDriverVehicleValid,
      this.isDettagliSinistroValid,
      this.isInvolvedVehiclesValid,
      this.isAccidentDynamicsValid,
      this.isDanniValid,
      this.isFotoValid(),
      this.isCidUploadValid()
    );
  }

  getProgressPercentage(): number {
    return this.stepValidator.getProgressPercentage(this.getCompletedSteps(), this.getTotalSteps());
  }

  // Metodo per iniziare un nuovo sinistro
  startNewSinistro() {
    window.location.reload();
  }

  // Metodo per preparare i dati per l'invio API
  prepareApiData(): any {
    const summary = this.getSummary();
    //const involvedVehicles = this.getInvolvedVehicles();
    
    return {
      newDriver: {
        id: summary.selectedDriverId,
        name: '', 
        surname: '',
        dateOfBirth: null,
        drivingLicence: '',
        driverCardNumber: '',
        phoneNumber: summary.telefono,
        driverVehicles: []
      },
      datecrash: summary.dataOra,
      vehicleA: {
        plate: summary.targa,
        circumstances: summary.vehicleA.circumstances
      },
      address: summary.indirizzo,
      lat: summary.latitudine,
      lon: summary.longitudine,
      wounded: summary.presenzaFeriti === 'true',
      physicalDamage: summary.danniMateriali === 'true',
      isWitnesses: summary.presenzaTestimoni === 'true',
      note: summary.note,
      liquidatedAmount: null,
      aniaExpiryDate: null,
      vehicleB: summary.vehicleB,
      fromApp: false
    };
  }

  private injectVehicleColors(): void {
    const involvedCount = this.getInvolvedVehiclesCount();
    this.vehicleColorsService.injectCSSVariables(involvedCount + 1); // +1 per il veicolo A ( il ns )
  }

  private getInvolvedVehiclesCount(): number {
    const involvedVehicles = this.involvedVehiclesForm?.get('involvedVehicles')?.value || [];
    return involvedVehicles.filter((v: any) => v.plate && v.plate.trim()).length;
  }

  getInvolvedVehicles(): any[] {
    return this.involvedVehiclesForm?.get('involvedVehicles')?.value?.filter((v: any) => v.plate && v.plate.trim()) || [];
  }

  goToStep(stepIndex: number): void {
    if (this.expansionPanels) {
      const panels = this.expansionPanels.toArray();
      // Chiudi tutti i pannelli
      panels.forEach(panel => panel.close());
      // Apri il pannello richiesto
      if (panels[stepIndex]) {
        panels[stepIndex].open();
      }
    }
  }

  private validateDriverForm(): boolean {
    return this.stepValidator.validateDriverForm(this.driverVehicleComponent, this.driverVehicleForm, this.crashesInfoData);
  }

  private capitalizeNames(input: string): string {
    return input
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  onBackFromSuccess(): void {
    this.showSuccessScreen = false;
    this.isSubmitted = false;
    if (this.stepSummaryComponent) {
      this.stepSummaryComponent.resetSubmitState();
    }
    this.startNewSinistro();
  }
}